import { forwardRef, memo } from 'react';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import RowOptionsMenu from '../../../../shared/components/RowOptionsMenu';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import type { GroupManagerGroup, GroupMember } from '../../../../services/GroupManager/groupManager.service';
import GroupTables from '../GroupTables';
import { GroupMemberAction, tableHeight } from '../GroupTables/columns';
import styles from './GroupPanel.module.scss';

interface GroupPanelProps {
  group: GroupManagerGroup;
  groups: Array<{ id: number; name: string }>;
  isOpen: boolean;
  onToggle: (groupId: number) => void;
  onEdit: (groupId: number) => void;
  onDelete: (groupId: number, anchor: HTMLElement | null) => void;
  onMemberAction: (action: GroupMemberAction, group: GroupManagerGroup, member: GroupMember, anchor: HTMLElement | null, targetGroupId?: number) => void;
}

enum GroupAction {
  EDIT = 'edit',
  DELETE = 'delete',
}

const GROUP_OPTIONS = [
  { id: GroupAction.EDIT, label: 'EDIT GROUP' },
  { id: GroupAction.DELETE, label: 'DELETE GROUP' },
];

const GroupPanel = forwardRef<HTMLElement, GroupPanelProps>(({ group, groups, isOpen, onToggle, onEdit, onDelete, onMemberAction }, ref) => {
  const regionId = `group-members-${group.id}`;

  const handleGroupOption = (actionId: string, trigger: HTMLElement | null) => {
    if (actionId === GroupAction.EDIT) onEdit(group.id);
    if (actionId === GroupAction.DELETE) onDelete(group.id, trigger);
  };

  return (
    <section ref={ref} className={styles.panel} data-group-id={group.id}>
      <h3 className={styles.panelHeading}>
        <button
          type="button"
          className={styles.panelButton}
          aria-expanded={isOpen}
          aria-controls={regionId}
          onClick={() => onToggle(group.id)}
        >
          {isOpen ? <DownOutlined className={styles.chevron} aria-hidden="true" /> : <RightOutlined className={styles.chevron} aria-hidden="true" />}
          <span className={styles.groupName}>{group.name}</span>
        </button>
        <RowOptionsMenu options={GROUP_OPTIONS} header="GROUP OPTIONS" onSelect={handleGroupOption} />
      </h3>
      <ConditionalRender visible={isOpen} hideFallback>
        <div id={regionId} className={styles.panelBody} style={{ maxHeight: tableHeight(group.members.length) }}>
          <GroupTables
            members={group.members}
            groups={groups}
            currentGroupId={group.id}
            onAction={(action, member, anchor, targetGroupId) => onMemberAction(action, group, member, anchor, targetGroupId)}
          />
        </div>
      </ConditionalRender>
    </section>
  );
});

GroupPanel.displayName = 'GroupPanel';
export default memo(GroupPanel);
